import { repositoryLabel } from '@/components/desktop-summary/summary-workspace-section';
import {
  CloudHandoffSourceMenu,
  type ExecutionMode,
  LocalBranchMenu,
  type SetupControlVariant,
  WorktreeStartingPointMenu,
} from '@/components/worktree-controls';
import { useAssistantOfConversation } from '@/features/assistants/use-assistant';
import {
  type LinkRepositoryHow,
  WhereItRunsPopover,
  type WhereItRunsProps,
  WhereItRunsSheet,
  type WhereRepository,
} from '@/features/new-conversation/where-it-runs';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import type { CloudHandoffSource } from '@/lib/desktop-shell/cloud-handoff';
import { selectWorktreeSourceRoot } from '@/lib/desktop-shell/conversation-worktree';
import { primaryCheckoutPath } from '@/lib/desktop-shell/execution-mode-controls';
import { shownConversationState } from '@/lib/desktop-shell/store';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { useWorkspaceName } from '@/lib/desktop-shell/use-workspace-name';
import { errorMessage } from '@/lib/error-message';
import type { BranchTarget } from '@/lib/native-desktop';
import type { SidebarThread } from '@/lib/sidebar-threads';
import type { SourceControlRepository } from '@/lib/source-control-connector';

/** The cloud's empty folder: a conversation without a repository, in the cloud. */
const EMPTY_SANDBOX_PATH = '/workspace';

export type ComposerMenuRowProps = {
  messageCount: number;
  selectedCloudRuntime: CloudRuntime | null;
  repositoryOptions: readonly WhereRepository[];
  /** The conversations, for the repositories used last. */
  threads: readonly SidebarThread[];
  busy: boolean;
  runtimeMode: ExecutionMode;
  cloudHandoffRepository: SourceControlRepository | null;
  cloudHandoffWorking: boolean;
  cloudHandoffBranches: readonly string[];
  cloudHandoffSource: CloudHandoffSource;
  /** A phone: "Where it runs" is a sheet. */
  phone: boolean;
  onStartProjectConversation: (path: string | null) => void;
  onStartPersonalConversation: () => void;
  onAddRepository: () => void;
  /** Gives a forge repository only the cloud has a folder of this computer. */
  onLinkRepository?: (repository: WhereRepository, how: LinkRepositoryHow) => void;
  onChangeExecutionMode: (mode: ExecutionMode) => void;
  onSwitchBranch: (target: BranchTarget) => void;
  onRefreshRemoteBranches: () => void;
  onOpenEnvironmentSettings: () => void;
  onLoadCloudHandoffOptions: () => Promise<void>;
  onCloudHandoffSourceChange: (source: CloudHandoffSource) => void;
  onCloudHandoffError: (message: string | null) => void;
};

/**
 * The destination chips of a new conversation's composer, after +, the model and the
 * permissions: its repository (which opens "Where it runs") and the branch it starts from (only
 * with a repository). The agent is the + menu's, as in a conversation. No assistant is chosen
 * here: an assistant is its conversation (its channel), and a new conversation is a plain one
 * (docs/adr-assistants.md). Once the conversation has messages they are gone: its header and
 * Summary show them.
 */
export function ComposerMenuRow(props: ComposerMenuRowProps) {
  const {
    messageCount,
    selectedCloudRuntime,
    repositoryOptions,
    threads,
    busy,
    runtimeMode,
    phone,
  } = props;
  const chatId = useDesktopShellStore((state) => state.chatId);
  const native = useDesktopShellStore((state) => state.native);
  const workspace = useDesktopShellStore((state) => state.workspace);
  const worktreeSourceRoot = useDesktopShellStore(selectWorktreeSourceRoot);
  const gitStatus = useDesktopShellStore((state) => state.gitStatus);
  const personalWorkspaceRoot = useDesktopShellStore((state) => state.personalWorkspaceRoot);
  const gitStatusState = useDesktopShellStore((state) => state.gitStatusState);
  const creatingWorktree = useDesktopShellStore(
    (state) => shownConversationState(state).creatingWorktree,
  );
  const workspaceEnvironment = useDesktopShellStore((state) => state.workspaceEnvironment);
  // A project's own conversation is filed and placed already: its header says where (N1).
  const project = useAssistantOfConversation(chatId);
  // The server's name for it (a worktree's is its repository's).
  const workspaceName = useWorkspaceName(workspace);
  if (messageCount > 0 || project !== null) return null;

  const repositoryRoot = worktreeSourceRoot ?? primaryCheckoutPath(gitStatus) ?? workspace;
  // A branch belongs to a repository: none without one (the personal folder, the empty sandbox).
  const hasRepository = hasChosenRepository(repositoryRoot, personalWorkspaceRoot);
  const repositoryName =
    repositoryRoot === null
      ? 'Repository'
      : // The personal folder and the cloud's empty sandbox are both "No repository" (the chip's
        // icon says where), never "workspace" (H2).
        hasRepository
        ? repositoryLabel(repositoryRoot, workspaceName)
        : 'No repository';
  const cloud = runtimeMode === 'cloud' || selectedCloudRuntime !== null;

  const where: WhereItRunsProps & { repositoryName: string } = {
    repositoryName,
    repositories: repositoryOptions.filter(
      (repository) => repository.path !== personalWorkspaceRoot,
    ),
    threads,
    currentPath: repositoryRoot,
    runtimeMode: cloud ? 'cloud' : runtimeMode,
    localAvailable: native === true,
    // A worktree is a copy of a repository: a plain folder has none to copy.
    worktreeAvailable: gitStatusState !== 'not-repository',
    disabled: busy || creatingWorktree,
    pending: creatingWorktree,
    // A provisioned cloud sandbox keeps its repository and place, as before.
    locked: selectedCloudRuntime !== null,
    onSelectRepository: (path) => {
      if (path !== repositoryRoot) props.onStartProjectConversation(path);
    },
    onAddRepository: props.onAddRepository,
    onLinkRepository: native === true ? props.onLinkRepository : undefined,
    // Always offered: the personal folder on a computer (made on first use), the cloud's empty
    // sandbox in a browser.
    noRepository: {
      selected: repositoryRoot !== null && !hasRepository,
      onSelect: () => {
        if (repositoryRoot !== null && !hasRepository) return;
        if (native === true) props.onStartPersonalConversation();
        else props.onStartProjectConversation(EMPTY_SANDBOX_PATH);
      },
    },
    onChangeMode: (mode) => {
      if (mode !== runtimeMode) props.onChangeExecutionMode(mode);
    },
    startFrom: hasRepository
      ? (variant) => <BranchControl {...props} cloud={cloud} variant={variant} />
      : undefined,
    environment:
      runtimeMode === 'worktree' && native === true
        ? { name: workspaceEnvironment?.name ?? null, onOpen: props.onOpenEnvironmentSettings }
        : undefined,
  };

  const rowClassName =
    'm-0 flex min-w-0 flex-wrap items-center gap-1.5 self-stretch border-0 px-3 pt-0 pb-1';
  if (phone)
    return (
      <fieldset aria-label="New conversation" className={rowClassName}>
        <WhereItRunsSheet {...where} />
      </fieldset>
    );
  return (
    <WhereItRunsPopover
      {...where}
      rowClassName={rowClassName}
      trailing={hasRepository ? <BranchControl {...props} cloud={cloud} variant="chip" /> : null}
    />
  );
}

/**
 * The branch a new conversation starts from, by where it runs: the checkout's branch (here),
 * the worktree's starting point, or the cloud sandbox's source branch.
 */
function BranchControl({
  cloud,
  variant,
  busy,
  selectedCloudRuntime,
  cloudHandoffRepository,
  cloudHandoffWorking,
  cloudHandoffBranches,
  cloudHandoffSource,
  onSwitchBranch,
  onRefreshRemoteBranches,
  onLoadCloudHandoffOptions,
  onCloudHandoffSourceChange,
  onCloudHandoffError,
}: ComposerMenuRowProps & { cloud: boolean; variant: Exclude<SetupControlVariant, 'inline'> }) {
  const native = useDesktopShellStore((state) => state.native);
  const executionMode = useDesktopShellStore((state) => state.executionMode);
  const gitStatus = useDesktopShellStore((state) => state.gitStatus);
  const creatingWorktree = useDesktopShellStore(
    (state) => shownConversationState(state).creatingWorktree,
  );
  const switchingBranch = useDesktopShellStore(
    (state) => shownConversationState(state).switchingBranch,
  );
  const worktreeStartingState = useDesktopShellStore((state) => state.worktreeStartingState);
  const localBranches = useDesktopShellStore((state) => state.localBranches);
  const setNewBranchName = useDesktopShellStore((state) => state.setNewBranchName);
  const setCreateBranchOpen = useDesktopShellStore((state) => state.setCreateBranchOpen);
  const setWorktreeStartingState = useDesktopShellStore((state) => state.setWorktreeStartingState);
  if (native !== true) return null;
  if (cloud)
    return gitStatus !== null || executionMode === 'cloud' ? (
      <CloudHandoffSourceMenu
        currentBranch={gitStatus?.branch ?? ''}
        defaultBranch={gitStatus?.defaultBranch ?? cloudHandoffRepository?.defaultBranch ?? null}
        disabled={busy || cloudHandoffWorking}
        localBranches={gitStatus?.branches ?? localBranches}
        onOpen={() => {
          void onLoadCloudHandoffOptions().catch((reason: unknown) => {
            onCloudHandoffError(errorMessage(reason));
          });
        }}
        onSourceChange={onCloudHandoffSourceChange}
        remoteBranches={cloudHandoffBranches}
        source={cloudHandoffSource}
        variant={variant}
      />
    ) : null;
  if (selectedCloudRuntime !== null || gitStatus === null) return null;
  if (executionMode === 'worktree')
    return (
      <WorktreeStartingPointMenu
        disabled={busy || creatingWorktree}
        gitStatus={gitStatus}
        onChange={setWorktreeStartingState}
        onOpen={onRefreshRemoteBranches}
        value={worktreeStartingState}
        variant={variant}
      />
    );
  return (
    <LocalBranchMenu
      disabled={busy || switchingBranch}
      gitStatus={gitStatus}
      onCreate={() => {
        setNewBranchName('');
        setCreateBranchOpen(true);
      }}
      onOpen={onRefreshRemoteBranches}
      onSwitch={onSwitchBranch}
      pending={switchingBranch}
      variant={variant}
    />
  );
}

/**
 * A repository is chosen: the folder is neither missing, the personal folder (No repository) nor
 * the cloud's empty sandbox.
 */
function hasChosenRepository(root: string | null, personalWorkspaceRoot: string | null): boolean {
  return (
    root !== null && root !== personalWorkspaceRoot && repositoryLabel(root) !== 'Empty sandbox'
  );
}
